import {ReactNode, useContext, useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import {Grid, Icon, Typography, Box, IconButton} from '@mui/material';

import {boardViewStyles, buttonsStyle, paperStyle, typographyColors} from './InboxSectionStyles';
import inbox_light from '../../../../../../assets/inbox/inbox-light.png';
import {EmpionButton} from '../../../../../../components/EmpionButton/EmpionButton';
import EmpionDialog from '../../../../../../components/EmpionDialog/EmpionDialog';
import {EmpionPaper} from '../../../../../../components/EmpionPaper/EmpionPaper';
import MatchTags from '../../../../../../components/MatchTags/MatchTags';
import {RejectionReasonModal} from '../../../../../../components/RejectionReasonModal/RejectionReasonModal';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {useApiWrite} from '../../../../../../hooks/useApiWrite';
import {greenColors} from '../../../../../../layouts/theme/designSystem';
import {Patch} from '../../../../../../lib/api/PATCH/Patch';
import {PatchRoutes} from '../../../../../../lib/api/PATCH/PatchRoutes';
import {AppConfig} from '../../../../../../lib/AppConfig';
import {MatchStatus} from '../../../../../../types/MatchStatus/MatchStatus';
import {MatchData, SupportedTagBE} from '../../../../../../types/shared-recruiting-types';
import {transformDate} from '../../../../../../utils/dateUtils/dateFormattingUtils';
import CardView from '../../../applicants/components/CardView';
import {DetailedCardView} from '../../../applicants/components/DetailedCardView/DetailedCardView';
import {DepartmentDisplay} from '../../../job-postings/components/DepartmentsDisplay';

interface InboxSectionProps {
  inboxItem: MatchData;
  icon: ReactNode;
  selected: string;
  companyId: string;
  initializeData: () => void;
  updateMatchTags: (status: string, match: MatchData) => void;
}

export default function InboxSection({
  inboxItem,
  icon,
  selected,
  companyId,
  initializeData,
  updateMatchTags,
}: InboxSectionProps) {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const {dispatchMessage} = useContext(SnackBarContext);

  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState<boolean>(false);
  const [isDetailedCardViewOpen, setIsDetailedCardViewOpen] = useState<boolean>(false);

  const onDeleteTagCallback = (tag: SupportedTagBE) => {
    updateMatchTags(selected, {...inboxItem, tags: inboxItem.tags?.filter((item) => item.tagLabel !== tag.tagLabel)});
  };
  const onAddTagCallback = (tag: SupportedTagBE) => {
    updateMatchTags(selected, {...inboxItem, tags: [...inboxItem.tags, tag]});
  };

  const {
    makeRequest: makeRequestContact,
    isLoading: isContactLoading,
    error: isContactError,
  } = useApiWrite<Patch.ContactMatch>({
    method: 'patch',
    path: PatchRoutes[Patch.ContactMatch](companyId, inboxItem.resourceId),
    onSuccess: () => {
      dispatchMessage(t('shared.tags.statusUpdate'), 'success');
      initializeData();
      setIsRejectionModalOpen(false);
    },
  });

  const {
    makeRequest: makeRequestInterview,
    isLoading: isInterviewLoading,
    error: isInterviewError,
  } = useApiWrite<Patch.InterviewMatch>({
    method: 'patch',
    path: PatchRoutes[Patch.InterviewMatch](companyId, inboxItem.resourceId),
    onSuccess: () => {
      dispatchMessage(t('shared.tags.statusUpdate'), 'success');
      initializeData();
      setIsRejectionModalOpen(false);
    },
  });

  const {
    makeRequest: makeRequestHire,
    isLoading: isHireLoading,
    error: isHireError,
  } = useApiWrite<Patch.HireMatch>({
    method: 'patch',
    path: PatchRoutes[Patch.HireMatch](companyId, inboxItem.resourceId),
    onSuccess: () => {
      dispatchMessage(t('shared.tags.statusUpdate'), 'success');
      initializeData();
      setIsRejectionModalOpen(false);
    },
  });

  const handleRejectSuccess = () => {
    dispatchMessage(t('shared.tags.statusUpdate'), 'success');
    setIsRejectionModalOpen(false);
    initializeData();
  };

  const {makeRequest, isLoading, error} = useApiWrite<Patch.RejectMatchByCompany>({
    method: 'patch',
    path: PatchRoutes[Patch.RejectMatchByCompany](companyId, inboxItem.resourceId),
    onSuccess: handleRejectSuccess,
  });

  useEffect(() => {
    if (isContactError || isInterviewError || isHireError) {
      dispatchMessage(t('shared.tags.error'), 'error');
    }
  }, [isContactError, isInterviewError, isHireError, dispatchMessage, t]);

  const renderButtonText = () => {
    switch (selected) {
      case MatchStatus.Accepted:
        return t('companyInbox.inboxButtons.contact');
      case MatchStatus.Contacted:
        return t('companyInbox.inboxButtons.interview');
      default:
        return undefined;
    }
  };

  const getLocations = () => {
    if (inboxItem.companyJobPosting?.locations && inboxItem.companyJobPosting.remoteHiring.allowed) {
      return inboxItem.companyJobPosting.locations.map((location) => location.city).join(', ') + ', Remote';
    } else if (inboxItem.companyJobPosting?.locations) {
      return inboxItem.companyJobPosting.locations.map((location) => location.city).join(', ');
    }

    return '';
  };

  const handleButtonAction = () => {
    if (isContactLoading || isInterviewLoading) return;
    if (selected === MatchStatus.Accepted) {
      makeRequestContact();
    } else {
      makeRequestInterview();
    }
  };

  const refreshMatchTags = (match: MatchData) => {
    updateMatchTags(selected, match);
  };

  const redirectToJobPosting = () => {
    const jobPostingId = inboxItem.companyJobPosting?.resourceId;

    const path = `${AppConfig.COMPANY_ADMIN_RECRUITING_JOB_POSTINGS_PATH}?id=${jobPostingId}`;
    navigate(path);
  };

  return (
    <EmpionPaper elevation={0} sx={paperStyle}>
      <Grid
        container
        padding={{
          xs: 1,
          sm: 2,
          md: 3,
          lg: 4,
        }}
      >
        <Grid container>
          <Grid item xs={12} sm={7} md={8}>
            <Grid
              item
              xs={12}
              sx={{
                display: {xs: 'block', sm: 'block', md: 'flex'},
                gap: '8px',
              }}
            >
              <Grid item xs={12} md={2} display={'flex'}>
                {selected === MatchStatus.Accepted ? (
                  <Box
                    component={'img'}
                    src={inbox_light}
                    sx={{marginRight: '6px', height: '16px', width: '24px', marginTop: '6px'}}
                    alt='inbox icon'
                  />
                ) : (
                  <Icon sx={{color: greenColors[70], marginRight: '4px'}}>{icon}</Icon>
                )}

                <Typography
                  variant='body1'
                  sx={{
                    color: greenColors[100],
                  }}
                >
                  {transformDate(inboxItem.updatedAt, 'numeric')}
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}
                ml={{xs: 0, md: 4, lg: 1}}
              >
                <MatchTags
                  match={inboxItem}
                  companyId={companyId}
                  onDeleteTagCallback={onDeleteTagCallback}
                  onAddTagCallback={onAddTagCallback}
                />
              </Grid>
            </Grid>
            <Grid>
              <Typography
                variant='h3'
                gutterBottom
                marginTop={1}
                onClick={redirectToJobPosting}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  },
                }}
              >
                {inboxItem.companyJobPosting?.title}
              </Typography>
            </Grid>
            <DepartmentDisplay departments={inboxItem.companyJobPosting?.departments || []} />
            <Grid>
              <Typography variant='body1' sx={typographyColors}>
                {getLocations()}
              </Typography>
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            sm={5}
            md={4}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: {xs: 'center', sm: 'flex-end'},
              marginTop: {xs: '16px', sm: '0'},
            }}
          >
            <EmpionPaper
              sx={boardViewStyles}
              onClick={() => {
                setIsDetailedCardViewOpen(true);
              }}
            >
              <CardView applicant={inboxItem.applicant} />
            </EmpionPaper>
            <Grid
              sx={{
                display: 'flex',
                height: 'fit-content',
                gap: '16px',
                justifyContent: 'space-between',
              }}
            >
              <EmpionButton
                sx={buttonsStyle}
                onClick={() => {
                  setIsRejectionModalOpen(true);
                }}
              >
                {t('companyInbox.inboxButtons.reject')}
              </EmpionButton>
              {selected === MatchStatus.Interview ? (
                <EmpionButton
                  onClick={() => {
                    if (isHireLoading) return;
                    makeRequestHire();
                  }}
                  sx={{
                    width: {xs: '100%', sm: '100%', md: '168px'},
                    boxShadow: 'none',
                  }}
                >
                  {t('companyInbox.inboxButtons.hire')}
                </EmpionButton>
              ) : (
                <EmpionButton
                  sx={buttonsStyle}
                  onClick={() => {
                    handleButtonAction();
                  }}
                >
                  {renderButtonText()}
                </EmpionButton>
              )}
            </Grid>
          </Grid>
        </Grid>
        <EmpionDialog
          open={isRejectionModalOpen}
          onClose={() => {
            setIsRejectionModalOpen(false);
          }}
        >
          <Grid container justifyContent={'flex-end'}>
            <IconButton
              onClick={() => {
                setIsRejectionModalOpen(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
          <RejectionReasonModal
            rejectionType='company'
            onReject={makeRequest}
            isUpdatingMatch={isLoading}
            error={error}
          />
        </EmpionDialog>

        <EmpionDialog
          open={isDetailedCardViewOpen}
          onClose={() => {
            setIsDetailedCardViewOpen(false);
          }}
        >
          <Grid container justifyContent={'flex-end'}>
            <IconButton
              onClick={() => {
                setIsDetailedCardViewOpen(false);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
          <DetailedCardView
            matchId={inboxItem.resourceId}
            onSuccess={initializeData}
            refreshMatchTags={refreshMatchTags}
          />
        </EmpionDialog>
      </Grid>
    </EmpionPaper>
  );
}
